import {
  API_ROUTES,
  customerDetailSchema,
  customerRowSchema,
  jobDetailSchema,
  jobRowSchema,
  equipmentDetailSchema,
  equipmentRowSchema,
  integrationConnectionSchema,
  syncSummarySchema,
  type SyncSummary,
} from '@hvac/contracts';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiClient } from './queries.js';

/** Customers and jobs, read-first object queries. */

export const partiesKeys = {
  customers: (organizationId: string) => ['customers', organizationId] as const,
  customer: (organizationId: string, customerId: string) =>
    ['customers', organizationId, customerId] as const,
  jobs: (organizationId: string) => ['jobs', organizationId] as const,
  job: (organizationId: string, jobId: string) => ['jobs', organizationId, jobId] as const,
} as const;

export function customersQuery(organizationId: string) {
  return queryOptions({
    queryKey: partiesKeys.customers(organizationId),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.customers.list(organizationId),
        schema: z.array(customerRowSchema),
        signal,
      }),
    staleTime: 30_000,
  });
}

export function customerQuery(organizationId: string, customerId: string) {
  return queryOptions({
    queryKey: partiesKeys.customer(organizationId, customerId),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.customers.detail(organizationId, customerId),
        schema: customerDetailSchema,
        signal,
      }),
  });
}

export function jobsQuery(organizationId: string) {
  return queryOptions({
    queryKey: partiesKeys.jobs(organizationId),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.jobs.list(organizationId),
        schema: z.array(jobRowSchema),
        signal,
      }),
    staleTime: 30_000,
  });
}

export function jobQuery(organizationId: string, jobId: string) {
  return queryOptions({
    queryKey: partiesKeys.job(organizationId, jobId),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.jobs.detail(organizationId, jobId),
        schema: jobDetailSchema,
        signal,
      }),
  });
}

export function equipmentQuery(organizationId: string) {
  return queryOptions({
    queryKey: ['equipment', organizationId] as const,
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.equipment.list(organizationId),
        schema: z.array(equipmentRowSchema),
        signal,
      }),
    staleTime: 30_000,
  });
}

export function equipmentDetailQuery(organizationId: string, equipmentId: string) {
  return queryOptions({
    queryKey: ['equipment', organizationId, equipmentId] as const,
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.equipment.detail(organizationId, equipmentId),
        schema: equipmentDetailSchema,
        signal,
      }),
  });
}

export async function syncHousecall(organizationId: string): Promise<SyncSummary> {
  return apiClient.request({
    path: API_ROUTES.integrations.housecallSync(organizationId),
    method: 'POST',
    schema: syncSummarySchema,
  });
}

export const housecallConnectionQuery = (organizationId: string) =>
  queryOptions({
    queryKey: ['integrations', organizationId, 'housecall'] as const,
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.integrations.housecallConnection(organizationId),
        schema: integrationConnectionSchema.nullable(),
        signal,
      }),
    staleTime: 30_000,
  });

export function connectHousecall(organizationId: string, apiKey: string) {
  return apiClient.request({
    path: API_ROUTES.integrations.housecallConnection(organizationId),
    method: 'POST',
    body: { apiKey },
    schema: integrationConnectionSchema,
  });
}

export function disconnectHousecall(organizationId: string) {
  return apiClient.request({
    path: API_ROUTES.integrations.housecallConnection(organizationId),
    method: 'DELETE',
    schema: z.object({ disconnected: z.boolean() }),
  });
}
